import { Divider, EHelpTextVariant, HelpText, TabBar } from '@outdoorsyco/bonfire';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import TripsLight from '@/assets/trips/Trips-Light.svg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import HeadMeta from '@/components/static-pages/HeadMeta';
import SwitchbackHeading from '@/components/switchback/Heading/Heading';
import Loading from '@/components/switchback/Loading/Loading';
import BookingCardContainer from '@/components/trips/BookingCard/BookingCardContainer';
import { ClaimsGuideModal } from '@/components/trips/ClaimsGuide';
import { TripGuideContainer } from '@/components/trips/TripGuideContainer';
import { PageEmptyState } from '@/components/ui/PageEmptyState';
import { EBookingsFilters } from '@/constants/booking';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { fetchBookings } from '@/redux/modules/bookings/actions';
import { TRootState } from '@/redux/rootReducer';
import { getParamAsString } from '@/utility/queryParams';
import { triggerLoginModal } from '@/utility/triggerLoginModal';

enum ETripsFilter {
  UPCOMING = 'upcoming',
  INACTIVE = 'inactive',
}

const upcomingTripsSelector = (state: TRootState) => [
  ...(state.bookings.bookings[EBookingsFilters.PENDING]?.data || []),
  ...(state.bookings.bookings[EBookingsFilters.UPCOMING]?.data || []),
];

const inactiveTripsSelector = (state: TRootState) => [
  ...(state.bookings.bookings[EBookingsFilters.COMPLETED]?.data || []),
  ...(state.bookings.bookings[EBookingsFilters.CANCELED]?.data || []),
  ...(state.bookings.bookings[EBookingsFilters.ABANDONED]?.data || []),
];

const tripsErrorSelector = (state: TRootState) =>
  Object.keys(state.bookings.bookings).some(v => {
    return state.bookings.bookings?.[v]?.error;
  });

const loadingTripsSelector = (state: TRootState) =>
  !Object.keys(state.bookings.bookings).length ||
  Object.keys(state.bookings.bookings).some(v => {
    return state.bookings.bookings?.[v]?.isFetching;
  });

type TEmptyStateProps = {
  isAuto?: boolean;
};

const EmptyState = ({ isAuto }: TEmptyStateProps) => (
  <PageEmptyState
    icon={isAuto ? null : <TripsLight className="h-auto w-[180px] lg:w-auto" />}
    title={<FormattedMessage defaultMessage="No trips yet" id="VrAdhd" />}
    description={
      isAuto ? (
        <FormattedMessage
          defaultMessage="This page feels lonely. Let’s plan your next trip."
          id="DeXyxG"
        />
      ) : (
        <FormattedMessage
          defaultMessage="This page feels lonely. Let’s plan your next adventure."
          id="zqRheX"
        />
      )
    }
    isAuto={isAuto}
  />
);

type TTripsInnerContentProps = {
  isAuto?: boolean;
};

const TripsInnerContent = ({ isAuto }: TTripsInnerContentProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const router = useRouter();
  const queryParams = useQueryParams();
  const { locale } = useLocaleSettings();

  const isAuthenticated = useSelector(selectAuthenticated);

  const upcomingTrips = useSelector(upcomingTripsSelector);
  const inactiveTrips = useSelector(inactiveTripsSelector);

  const tripsError = useSelector(tripsErrorSelector);
  const loadingTrips = useSelector(loadingTripsSelector);

  const queryFilter = getParamAsString(queryParams.filter);
  const filter =
    queryFilter && Object.values(ETripsFilter).includes(queryFilter as ETripsFilter)
      ? (queryFilter as ETripsFilter)
      : ETripsFilter.UPCOMING;

  const hasSomeNonStationaryRVBookings = upcomingTrips.some(booking => {
    return booking.rental_summary?.rental_category === 'rv' && !booking.delivery.stationary;
  });

  useEffect(() => {
    (async () => {
      if (isAuthenticated) {
        const types = isAuto ? ['auto'] : ['rv', 'stay', 'campground'];

        await Promise.all([
          dispatch(fetchBookings(EBookingsFilters.UPCOMING, types, locale)),
          dispatch(fetchBookings(EBookingsFilters.PENDING, types, locale)),
          dispatch(fetchBookings(EBookingsFilters.ABANDONED, types, locale)),
          dispatch(fetchBookings(EBookingsFilters.COMPLETED, types, locale)),
          dispatch(fetchBookings(EBookingsFilters.CANCELED, types, locale)),
        ]);
      }
    })();
  }, [isAuthenticated, dispatch, locale, isAuto]);

  useEffect(() => {
    if (!isAuthenticated) {
      triggerLoginModal(undefined, false);
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <section>
        <MaxWidth className="mx-auto my-8">
          <EmptyState isAuto={isAuto} />
        </MaxWidth>
      </section>
    );
  }

  if (tripsError) {
    return (
      <HelpText
        className="px-4 py-2 m-2"
        text={intl.formatMessage({
          defaultMessage: "It looks like we're having some issues. Please try again shortly.",
          id: 'hcZJ/o',
        })}
        variant={EHelpTextVariant.Error}
        showIcon
      />
    );
  }

  if (loadingTrips) {
    return <Loading />;
  }

  if (!upcomingTrips?.length && !inactiveTrips?.length) {
    return (
      <section>
        <MaxWidth className="mx-auto my-8">
          <EmptyState isAuto={isAuto} />
        </MaxWidth>
      </section>
    );
  }

  const tabItems = [
    { title: intl.formatMessage({ defaultMessage: 'Upcoming', id: '6/aEFZ' }) },
    { title: intl.formatMessage({ defaultMessage: 'Inactive', id: '6Tps09' }) },
  ];

  const title = intl.formatMessage({
    defaultMessage: 'Trips',
    id: 'XmO9g/',
  });

  return (
    <>
      <section>
        <MaxWidth className="mx-auto my-8 md:px-8" collapseMobile>
          <div className="mx-4 md:mx-0">
            <SwitchbackHeading
              level={2}
              className="mb-8 font-medium text-gray-900 font-highlight autoType800 md:mx-0">
              {title}
            </SwitchbackHeading>

            <TabBar className="mb-8" ariaLabel={title}>
              {tabItems.map((item, index) => (
                <TabBar.Item
                  key={index}
                  label={item.title}
                  active={
                    { [ETripsFilter.UPCOMING]: 0, [ETripsFilter.INACTIVE]: 1 }[filter] === index
                  }
                  onClick={() => {
                    router.push(
                      `${isAuto ? '/auto' : ''}/trips?filter=${[ETripsFilter.UPCOMING, ETripsFilter.INACTIVE][index]}`,
                      undefined,
                    );
                  }}
                />
              ))}
            </TabBar>
          </div>

          {filter === ETripsFilter.UPCOMING &&
            (upcomingTrips.length ? (
              <>
                <BookingCardContainer bookings={upcomingTrips} isAuto={isAuto} />

                {hasSomeNonStationaryRVBookings && (
                  <div className="ml-4 md:ml-0">
                    <Divider className="mb-8" />
                    {/** So far don't show any trip guides for the auto */}
                    {isAuto ? null : <TripGuideContainer />}
                  </div>
                )}
              </>
            ) : (
              <EmptyState isAuto={isAuto} />
            ))}

          <div className="mx-4 md:mx-0">
            {filter === ETripsFilter.INACTIVE &&
              (inactiveTrips.length ? (
                <BookingCardContainer bookings={inactiveTrips} isAuto={isAuto} />
              ) : (
                <div className="my-8 text-center">
                  <FormattedMessage defaultMessage="No inactive trips" id="S0jQkJ" />
                </div>
              ))}
          </div>
        </MaxWidth>
      </section>

      <ClaimsGuideModal />
    </>
  );
};

type TTripsIndexProps = {
  isAutoPage?: boolean;
};

export const TripsIndex = ({ isAutoPage = false }: TTripsIndexProps) => {
  const intl = useIntl();

  return (
    <>
      <HeadMeta
        title={intl.formatMessage({
          defaultMessage: 'Upcoming Trips',
          id: 'y1y+Ik',
        })}
        noindex
      />

      <TripsInnerContent isAuto={isAutoPage} />
    </>
  );
};
